/** One message per API error code: an `ApiError` is shown by its `code`, never by its English `detail`. */
export const errors = {
    VALIDATION_FAILED: "Please check the highlighted fields.",
    UNAUTHENTICATED: "Please sign in again.",
    FORBIDDEN: "You don't have permission to do this.",
    NOT_FOUND: "We couldn't find that. It may have been removed.",
    CONFLICT: "Someone else changed this. Reload and try again.",
    BUSINESS_RULE_VIOLATION: "This action isn't allowed.",
    RATE_LIMITED: "Too many attempts. Please wait a moment and try again.",
    INTERNAL: "Something went wrong on our side. Please try again.",
    SERVICE_UNAVAILABLE: "The service is temporarily unavailable. Please try again shortly.",
    INVALID_CREDENTIALS: "The email, phone or password is incorrect.",
    INVALID_REFRESH_TOKEN: "Your session has ended. Please sign in again.",
    ACCOUNT_LOCKED: "Too many failed attempts. Sign-in is locked for a while.",
    INVALID_OTP: "That code is wrong or has expired.",
    LAST_OWNER: "A business must keep at least one owner.",
    SYSTEM_ROLE_READ_ONLY: "Built-in roles can't be changed.",
    IDEMPOTENCY_KEY_REUSED: "This request was already sent with different details. Please start again.",
    FILE_REJECTED: "This file was rejected. Check its type and size.",
    PLAN_LIMIT_REACHED: "Your plan's limit is reached. Upgrade the plan to add more.",
    FEATURE_NOT_IN_PLAN: "Your plan doesn't include this feature. Upgrade the plan to use it.",
    PHONE_REGISTERED: "This mobile number already has an account. Sign in instead.",
    EMAIL_REGISTERED: "This email already has an account. Sign in instead.",
    LEGAL_OUTDATED: "The terms were just updated. Reload the page and accept the new version.",
    LEGAL_ACCEPTANCE_REQUIRED: "Accept the updated terms to continue.",
    SIGNUP_DISABLED: "New sign-ups are not open here.",
    TOTP_ENROLMENT_REQUIRED: "Turn on two-step sign-in to continue.",
    TOTP_NOT_ENROLLED: "Turn on your own two-step sign-in first; while it is required for everyone it cannot be turned off.",
    DOWNGRADE_BLOCKED: "Your business uses more than that plan allows. Archive or deactivate some first.",
    COUPON_INVALID: "This coupon can't be used here.",
    TRX_ID_USED: "This bKash transaction ID was already used.",
    REQUEST_PENDING: "A payment is already waiting for approval.",
    ACCOUNT_RESTRICTED: "Your payment is overdue: pay your plan to add more.",
    ACCOUNT_SUSPENDED: "Your account is suspended. Pay your plan to continue.",
    CLOSURE_CONFIRMATION_MISMATCH: "Type the business web address exactly to confirm.",
    DATA_EXPORT_RUNNING: "A data export is already being prepared. Wait for it to finish.",
    PASSWORD_CHANGE_REQUIRED: "Set your own password to continue.",
    STAFF_HAS_ACCOUNT: "This person already has a Sobthik account. Send them an invitation: they join when they accept it.",
    STAFF_ACCOUNT_SHARED: "This person has their own Sobthik account, so only they can change the password (with Forgot password).",
    CANNOT_DISABLE_SELF: "You cannot turn off your own access.",
    BARCODE_TAKEN: "This barcode already belongs to another product.",
    NO_BARCODE: "Some products have no barcode yet. Generate one for them first.",
    SKU_TAKEN: "This SKU is already used by another product.",
    PRICE_ABOVE_MRP: "The selling price can't be higher than the MRP.",
    CATEGORY_IN_USE: "This category still has products or subcategories. Move them first.",
    TOO_MANY_VARIANTS: "Too many variants: a product can have at most 250 combinations.",
    SEARCH_UNAVAILABLE: "Search is not available right now. The product list still works.",
    IMPORT_NOT_VALIDATED: "This import is not ready to apply (it is still being checked, or already applied or cancelled).",
    IMPORT_FILE_UNREADABLE: "The file could not be read. Use the template as an .xlsx or .csv file.",
    LOCATION_IN_USE: "This location still has stock or open transfers, so it can't be deactivated yet.",
    INSUFFICIENT_STOCK: "Not enough stock for this product at this location.",
    STOCK_NOT_TRACKED: "This product does not track stock.",
    LOCATION_INACTIVE: "This location is inactive. Activate it first.",
    OPENING_STOCK_EXISTS: "This product already has stock here. Use an adjustment instead.",
    PRODUCT_HAS_STOCK: "This product already has stock, so this can't be changed.",
    INVALID_TRANSITION: "This step isn't possible in the current state. Reload and try again.",
    COUNT_OVERLAP: "Some of these products are already in an open stock count at this location.",
    COUNT_EMPTY: "No stock-tracked products match. Change the location or the filter.",
    COUNT_TOO_LARGE: "Too many products for one count. Count by category or brand instead.",
    SUPPLIER_ARCHIVED: "This supplier is archived. Unarchive them first.",
    OVER_RECEIPT: "More than was ordered and is still open on the purchase order.",
    OVER_RETURN: "More than this receipt brought in, after what was already returned.",
    ALREADY_REVERSED: "This payment was already reversed.",
    NOTHING_DUE: "This customer owes nothing.",
    CREDIT_LIMIT_EXCEEDED: "This would take the customer over their credit limit.",
    VARIANT_NOT_SELLABLE: "This product is archived and cannot be sold.",
    INVALID_DISCOUNT: "The discount is more than the amount it applies to.",
    NOT_A_DUPLICATE: "These orders are not duplicates of each other.",
    NO_PHONE: "This order has no phone number to text.",
    AREA_IN_OTHER_ZONE: "This area already belongs to another delivery zone.",
    REFUND_TOO_LARGE: "The refund is more than the returned goods are worth.",
    ACCOUNT_ARCHIVED: "This payment account is archived.",
    COURIER_NOT_SUPPORTED: "This courier is not supported yet.",
    COURIER_ACCOUNT_ARCHIVED: "This courier account is archived.",
    NO_COURIER_ACCOUNT: "Connect a courier account first (or pick one).",
    ORDER_NOT_SHIPPABLE: "Only confirmed or packed orders with a delivery address can be booked.",
    SHIPMENT_EXISTS: "This order already has a courier booking.",
    COURIER_CANNOT_CANCEL: "This courier does not allow cancelling a booked parcel here; cancel it in the courier panel.",
    RTO_ALREADY_RECEIVED: "This returned parcel was already received.",
    SHIPMENT_NOT_RETURNABLE: "The courier delivered this parcel; record a customer return instead.",
    PAYOUT_EXISTS: "This payout was already imported for this courier.",
    PHONE_BLOCKED: "This number is already blocked.",
    DEVICE_REVOKED: "This device was removed by a manager. Pair it again to continue.",
    REGISTER_HAS_DEVICE: "This register already has a device. Revoke it first.",
    RESYNC_REQUIRED: "The data on this device is too old and must be downloaded again.",
    APP_UPGRADE_REQUIRED: "Update the POS app to continue syncing.",
    DOMAIN_TAKEN: "This domain is already connected to a shop.",
    DOMAIN_LIMIT_REACHED: "A shop can have at most 5 own domains. Remove one first.",
    SLUG_TAKEN: "Another product already uses this web address.",
    BOT_CHECK_FAILED: "We could not check that you are not a robot. Please try again.",
    SANDBOX_BUSY: "All demo shops are in use right now. Try again in a minute.",
    DEMO_SHOP: "This is a demo shop, so it takes no orders. Have a look around instead.",
    COUPON_NOT_APPLICABLE: "This coupon does not apply to this order.",
    COUPON_EXPIRED: "This coupon has expired.",
    COUPON_MIN_ORDER: "The order is below the coupon's minimum.",
    COUPON_LIMIT_REACHED: "This coupon has been used up. Remove it to order.",
    CATEGORY_ARCHIVED: "This expense category is archived.",
    REPORT_RANGE_TOO_LONG: "Choose a shorter period.",
    ACCOUNT_EXISTS: "There is already an account with this email or mobile number. Sign in instead.",
    SIGN_UP_OFF: "This shop does not take new accounts this way.",
    CODE_REQUIRED: "Confirm the code we sent you first.",
    CODE_SIGN_IN_OFF: "This shop does not offer sign-in with a code. Use your password.",
    PHONE_NOT_VERIFIED: "Verify your mobile number first.",
    INVALID_GOOGLE_TOKEN: "Google sign-in did not work. Please try again.",
    GOOGLE_SIGN_IN_OFF: "This shop does not offer sign-in with Google.",
    PASSWORD_REQUIRED: "Set a password first, so you can still sign in without Google.",
    ALREADY_IN_LIBRARY: "You have this already. Open it from your library.",
    SIGN_IN_REQUIRED: "Sign in first: digital products go to your account's library.",
};
//# sourceMappingURL=errors.js.map